import React, { useState, useEffect } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    LinearProgress,
    Alert,
    Snackbar,
    Tooltip,
    Typography,
    Chip
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import {
    Sync as SyncIcon,
} from '@mui/icons-material';
import { getTaxes, syncTaxes, getTaxSyncStatus } from '../../../shared/services/taxService';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { SyncProgressBanner, type SyncProgressDetails } from '../../../shared/components/SyncProgressBanner';
import { Tax as TaxType } from '../../../shared/types/tax';

export const Tax: React.FC = () => {
    const { hasPermission, isSuperAdmin } = usePermission();
    const [allTaxes, setAllTaxes] = useState<TaxType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [syncDetails, setSyncDetails] = useState<SyncProgressDetails | null>(null);

    useEffect(() => {
        loadTaxes();
    }, []);

    const loadTaxes = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getTaxes({
                page: 1,
                limit: 1000
            });

            if (response.success && response.data) {
                setAllTaxes(response.data.items || []);
            } else {
                setAllTaxes([]);
            }
        } catch (err: any) {
            console.error('Error loading taxes:', err);
            setError(err.response?.data?.message || 'Failed to load taxes');
            setAllTaxes([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredTaxes = React.useMemo(() => {
        if (!searchInput.trim()) return allTaxes;
        const q = searchInput.trim().toLowerCase();
        return allTaxes.filter(tax =>
            (tax.name?.toLowerCase() || '').includes(q) ||
            (tax.taxId?.toString().toLowerCase() || '').includes(q) ||
            (tax.type?.toLowerCase() || '').includes(q)
        );
    }, [allTaxes, searchInput]);

    const paginatedTaxes = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredTaxes.slice(start, start + pageSize);
    }, [filteredTaxes, currentPage, pageSize]);

    const handleSync = async () => {
        let pollInterval: any;
        try {
            setSyncing(true);
            setError('');
            setSyncDetails(null);

            await syncTaxes();

            const checkStatus = async () => {
                try {
                    const statusRes = await getTaxSyncStatus();
                    if (statusRes.success) {
                        setSyncDetails(statusRes.data as any);

                        if (statusRes.data.isRunning) {
                            await loadTaxes();
                        } else {
                            if (pollInterval) clearInterval(pollInterval);
                            setSyncing(false);
                            if (statusRes.data.lastError) {
                                setError(statusRes.data.lastError);
                            } else {
                                setSuccess('Taxes synced successfully');
                            }
                            await loadTaxes();
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    if (pollInterval) clearInterval(pollInterval);
                    setSyncing(false);
                }
            };

            await checkStatus();
            pollInterval = setInterval(checkStatus, 3000);

        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to start sync');
            setSyncing(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Taxes
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search taxes"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />
                    {(isSuperAdmin() || hasPermission('tax.sync')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<SyncIcon />}
                            onClick={handleSync}
                            disabled={syncing || loading}
                            loading={syncing}
                        >
                            {syncing ? 'Syncing...' : 'Sync Taxes'}
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {/* Sync Progress Banner */}
                    <SyncProgressBanner syncing={syncing} details={syncDetails} />

                    {loading && allTaxes.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && allTaxes.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : filteredTaxes.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() ? 'No taxes match your search.' : 'No taxes found.'}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Tax Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Type</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700 text-center">Percentage</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Tax Components</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Last Synced</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredTaxes.length === 0}
                                emptyColSpan={5}
                                emptyMessage={searchInput.trim() ? 'No taxes match your search.' : 'No taxes in this page.'}
                            >
                                {paginatedTaxes.map((tax) => (
                                    <TableRow key={tax.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {tax.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {tax.taxId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{tax.type}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={`${tax.percentage}%`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {tax.taxGroups.map((group, index) => (
                                                    group.value > 0 && (
                                                        <Chip
                                                            key={index}
                                                            label={`${group.type}: ${group.value}%`}
                                                            size="small"
                                                            variant="filled"
                                                            sx={{ fontSize: '0.65rem' }}
                                                        />
                                                    )
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {tax.updatedAt ? new Date(tax.updatedAt).toLocaleString() : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={filteredTaxes.length}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
